<template>
  <div
    ref="groupRoot"
    class="sp-interactive-grid-group"
    :class="classModifiers"
    :data-items-per-row="itemsPerRow"
    v-bind="$attrs"
  >
    <slot />
  </div>

  <sp-modal
    :modal-title="activeCard.title"
    :title-oneline="false"
    :open="isModalOpen"
    :width="`var(--modal-width)`"
    :class="modalClassModifiers"
    @update-open="handleModalUpdate"
    @close="handleModalClose"
  >
    <div v-if="activeCard.subtitle" slot="subtitle" class="modal-subtitle">{{ activeCard.subtitle }}</div>

    <div ref="modalContentContainer" class="modal-content-wrapper"></div>

    <sp-card-actions v-if="hasMultipleCards" slot="actions" class="modal-actions-container">
      <div class="modal-actions">
        <sp-spacer v-if="isFirst"></sp-spacer>
        <sp-button v-else variant="outlined" class="modal-actions-button" :size="buttonSize" @click="goToPrevious">
          Previous
        </sp-button>

        <sp-button v-if="!isLast" class="modal-actions-button" :size="buttonSize" @click="goToNext"> Next </sp-button>
        <sp-button
          v-else-if="hasFinalAction"
          tag="a"
          class="modal-actions-button"
          :size="buttonSize"
          :href="finalActionHref"
          :target="finalActionTarget"
        >
          {{ finalActionLabel }}
        </sp-button>
        <sp-button v-else class="modal-actions-button" :size="buttonSize" @click="handleModalClose"> Done </sp-button>
      </div>
    </sp-card-actions>
  </sp-modal>

  <div ref="measureHeightElement" class="measure-height-element" aria-hidden="true"></div>
</template>

<script setup>
import { ref, computed, watch, nextTick, useTemplateRef } from "vue";

// TODO(#26705): drop once the template is restructured to a single root node
// (multiple roots — main div + sp-modal + measure div — break Vue 3's automatic
// attr inheritance, so $attrs is bound explicitly to the main root above)
defineOptions({ inheritAttrs: false });
import { onKeyStroke, useEventListener, useMutationObserver } from "@vueuse/core";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { getSlotContent } from "#ce/utils/slots";
import { props as propsDefinitions } from "./props/grid-group.js";

const props = defineProps(propsDefinitions);

const { isSmScreen, breakpointModifiers } = useBreakpoints();

const groupRoot = useTemplateRef("groupRoot");
const cardElements = ref([]);
const isModalOpen = ref(false);
const currentIndex = ref(-1);
const lastFocusedElement = ref(null);
const modalContentContainer = useTemplateRef("modalContentContainer");

const measureHeightElement = useTemplateRef("measureHeightElement");
const maxContentHeight = ref(0);

// Vue reflects CE prop writes back to the attribute, so observing attributes covers both.
const cardAttributes = ref([]);

function readCardAttributes() {
  cardAttributes.value = cardElements.value.map((el) => ({
    title: el.getAttribute("title") ?? "",
    subtitle: el.getAttribute("subtitle") ?? "",
  }));
}

useMutationObserver(cardElements, readCardAttributes, {
  attributes: true,
  attributeFilter: ["title", "subtitle"],
});

const activeCard = computed(() => cardAttributes.value[currentIndex.value] ?? {});
const isFirst = computed(() => currentIndex.value <= 0);
const isLast = computed(() => currentIndex.value >= cardElements.value.length - 1);
const hasMultipleCards = computed(() => cardElements.value.length > 1);
const hasFinalAction = computed(() => Boolean(props.finalActionHref && props.finalActionLabel));

const classModifiers = computed(() => [
  ...breakpointModifiers.value,
  {
    [`--items-per-row-${props.itemsPerRow}`]: props.itemsPerRow !== "auto",
  },
]);

const modalClassModifiers = computed(() => [...breakpointModifiers.value]);

const buttonSize = computed(() => (isSmScreen.value ? "small" : "default"));

onKeyStroke("ArrowLeft", () => {
  if (isModalOpen.value && !isFirst.value) {
    goToPrevious();
  }
});

onKeyStroke("ArrowRight", () => {
  if (isModalOpen.value && !isLast.value) {
    goToNext();
  }
});

watch(groupRoot, () => {
  cardElements.value = getSlotContent(groupRoot.value, { flatten: true }).filter(
    (node) => node.tagName === "SP-INTERACTIVE-CARD",
  );
});

watch(cardElements, async () => {
  readCardAttributes();

  await nextTick();
  updateChildrenContext();
  calculateTallestContentHeight();

  registerEventListeners();
});

watch(() => props.itemsPerRow, updateChildrenContext);

function updateChildrenContext() {
  cardElements.value.forEach((cardEl) => {
    cardEl.setInteractionMode?.("modal");
  });
}

function registerEventListeners() {
  cardElements.value.forEach((cardEl, index) => {
    useEventListener(
      cardEl,
      "click",
      (event) => {
        openStepperModal(event, index);
      },
      { capture: true },
    );

    useEventListener(
      cardEl,
      "keydown",
      (event) => {
        if (event.key === "Enter" || event.key === " ") {
          openStepperModal(event, index);
        }
      },
      { capture: true },
    );
  });
}

/**
 * DOM reparenting logic to handle moving DOM nodes:
 * Instead of using `innerHTML`, this avoids the security risks,
 * and allows to temporarily move actual DOM nodes from card's slot
 * into the modal when it opens up, and move them back when it closes
 * or when user navigates to the next/previous card.
 */
function moveContent(from, to) {
  if (!from || !to) {
    return;
  }
  to.replaceChildren(...from.childNodes);
}

async function openStepperModal(event, index) {
  lastFocusedElement.value = event?.target ?? document.activeElement;
  event?.preventDefault();
  event?.stopPropagation();
  currentIndex.value = index;
  isModalOpen.value = true;

  await nextTick();
  const cardEl = cardElements.value[index];
  moveContent(cardEl, modalContentContainer.value);
}

function handleModalUpdate(event) {
  isModalOpen.value = event.detail[0];
}

function handleModalClose() {
  const cardEl = cardElements.value[currentIndex.value];
  if (modalContentContainer.value?.childNodes.length) {
    moveContent(modalContentContainer.value, cardEl);
  }

  isModalOpen.value = false;
  if (lastFocusedElement.value) {
    lastFocusedElement.value.focus();
    lastFocusedElement.value = null;
  }
}

function switchTo(newIndex) {
  const oldCardEl = cardElements.value[currentIndex.value];
  moveContent(modalContentContainer.value, oldCardEl);

  currentIndex.value = newIndex;

  const newCardEl = cardElements.value[newIndex];
  moveContent(newCardEl, modalContentContainer.value);
  modalContentContainer.value.scrollTop = 0;
  modalContentContainer.value.scrollIntoView({ block: "start" });
}

function goToNext() {
  if (!isLast.value) {
    switchTo(currentIndex.value + 1);
  }
}

function goToPrevious() {
  if (!isFirst.value) {
    switchTo(currentIndex.value - 1);
  }
}

/**
 * Calculate the height needed for the tallest content and use that
 * as a fixed height for all modal steps to prevent jumping.
 */
function calculateTallestContentHeight() {
  if (!measureHeightElement.value || !cardElements.value.length) {
    return;
  }

  const heights = cardElements.value.map((el) => {
    measureHeightElement.value.replaceChildren(...el.cloneNode(true).childNodes);
    return measureHeightElement.value.scrollHeight;
  });

  maxContentHeight.value = Math.max(...heights);
}

const modalContentHeight = computed(() => {
  if (isSmScreen.value) {
    return "auto";
  }

  const max = Math.round(window.innerHeight * 0.7);
  const min = Math.round(window.innerHeight * 0.25);
  const content = `${maxContentHeight.value}px + var(--sp-comp-interactive-modal-content-offset, var(--sp-ref-scale-96))`;

  return `clamp(${min}px, calc(${content}), ${max}px)`;
});
</script>

<style lang="scss">
@import "./shared/interactive-theme";

:host {
  /* Layout */
  --group-gap: var(--sp-comp-interactive-group-gap, var(--sp-ref-scale-24));
  --card-min-width: var(--sp-comp-interactive-card-min-width, 17.5rem);

  display: block;
}

:host sp-modal sp-card-actions {
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-interactive-grid-group {
  display: grid;
  gap: var(--group-gap);
  grid-template-columns: repeat(auto-fill, minmax(var(--card-min-width), 1fr));
  justify-items: stretch;
  align-items: start;

  @for $i from 1 through 4 {
    &.--items-per-row-#{$i} {
      grid-template-columns: repeat(#{$i}, 1fr);
    }
  }

  &.--is-sm-screen {
    grid-template-columns: 1fr;
  }
  &.--is-md-screen,
  &.--is-lg-screen {
    &.--items-per-row-4 {
      grid-template-columns: 1fr 1fr;
    }
  }
}

.modal-content-wrapper {
  height: v-bind(modalContentHeight);
}

.modal-actions-container {
  padding: var(--modal-actions-padding);
}

.modal-actions {
  display: flex;
  gap: var(--modal-actions-gap);
  width: 100%;

  &-button {
    flex: 1;
  }
}

sp-button {
  --disabled-background-color: var(
    --sp-comp-interactive-modal-actions-button-disabled-background-color,
    var(--sp-sys-color-disabled)
  );
  --disabled-text-color: var(
    --sp-comp-interactive-modal-actions-button-disabled-text-color,
    var(--sp-sys-color-on-disabled)
  );
}

sp-modal {
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;

  &.--is-sm-screen {
    .modal-content-wrapper {
      padding-bottom: var(--sp-ref-scale-64) !important;
    }

    .modal-actions-container {
      padding: var(--sp-ref-scale-16) !important;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: calc(100dvw - var(--sp-ref-scale-32));
      max-width: 100%;
      background-color: var(--modal-header-background-color, var(--sp-sys-color-white));
      box-shadow: var(--modal-box-shadow);
      z-index: 1000;
    }
  }
}

.measure-height-element {
  position: absolute;
  top: -9999px;
  left: -9999px;
  width: calc(var(--modal-width) - var(--sp-comp-interactive-modal-content-padding-inline, 1rem) * 2);
  max-width: 90vw;
  visibility: hidden;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  box-sizing: border-box;
}
</style>
